/**
 * FamilyPlanPage — The Family Room.
 *
 * Residence Journal design language, chief-of-staff mode.
 * Presentation only — every handler, testid, seed constant, editable state
 * and toast is preserved from the pre-renovation implementation.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { usePersistentState } from "./state/usePersistentState";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  ModuleMark, Caption, Rule, TextLink, PillButton, Colophon,
  useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

/* ── Seed content ─────────────────────────────────────────────────────── */

const SEED_CHARTER = {
  charter:
    "We are a whānau that runs the household together. Each member contributes, each member is heard, and decisions are made calmly with a shared plan.",
  vision:
    "A home that is calm, organised and full of warmth — where every person can grow, contribute and feel safe.",
  mission:
    "Plan the week together, keep agreements, look after each other, and protect time for what matters most.",
};

const SEED_VALUES = [
  { id: "faith",          label: "Faith",          line: "Something bigger than ourselves." },
  { id: "respect",        label: "Respect",        line: "Listen before reacting." },
  { id: "responsibility", label: "Responsibility", line: "Do what we said we would." },
  { id: "health",         label: "Health",         line: "Look after body and mind." },
  { id: "learning",       label: "Learning",       line: "Stay curious." },
  { id: "stewardship",    label: "Stewardship",    line: "Care for what we hold." },
  { id: "service",        label: "Service",        line: "Help where we can." },
  { id: "joy",            label: "Joy",            line: "Make room for laughter." },
];

const SEED_GOALS = [];

const SEED_RESPONSIBILITIES = [];

const SEED_AGREEMENTS = [
  { id: "a1", category: "Respect and conflict",    line: "Disagree without raising voices. Don't go to bed angry." },
  { id: "a2", category: "Devices and screen time", line: "Phones off the table at meals. Devices downstairs after 9pm." },
  { id: "a3", category: "Shared spaces",           line: "If you used it, you tidy it. Kitchen reset every night." },
  { id: "a4", category: "Chores",                  line: "Zones are owned. Daily resets before screens." },
  { id: "a5", category: "Money",                   line: "Big purchases get a family conversation. Pocket money on Sundays." },
  { id: "a6", category: "Visitors",                line: "Friends welcome — a day's notice where possible." },
  { id: "a7", category: "Bedtime routines",        line: "Kids in bed by 9pm school nights. Adults by 10:30pm." },
  { id: "a8", category: "Sunday rhythm",           line: "Rest, family time, a slow start to the week." },
];

const SEED_MEETINGS = [];

const SEED_DECISIONS = [];

const STATUS_META = {
  not_started: { label: "Not started" },
  in_progress: { label: "In progress" },
  complete:    { label: "Complete" },
};

/* ── Editable charter block ───────────────────────────────────────────── */

function CharterBlock({ label, value, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <div
        className="pt-5 pb-5 border-t"
        style={{ borderColor: 'var(--ara-line-soft)' }}
        data-testid={testId}
      >
        <div className="flex items-baseline justify-between gap-4">
          <Caption>{label}</Caption>
          <TextLink testid={`${testId}-edit`} onClick={() => { setDraft(value); setEditing(true); }}>
            Edit
          </TextLink>
        </div>
        <p className="mt-3 text-[17px] leading-[1.5] tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '48ch' }}>
          {value}
        </p>
      </div>
    );
  }

  return (
    <div className="pt-5 pb-5 border-t" style={{ borderColor: 'var(--ara-line-soft)' }}>
      <Caption>{label}</Caption>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={4}
        className="mt-3 w-full bg-transparent outline-none text-[16px] leading-[1.5] tracking-[-0.005em] resize-none"
        style={{ color: 'var(--ara-ink)', borderBottom: '1px solid var(--ara-ink)', padding: '4px 0' }}
        autoFocus
      />
      <div className="mt-3 flex items-center gap-6">
        <TextLink onClick={() => setEditing(false)}>Cancel</TextLink>
        <TextLink
          testid={`${testId}-save`}
          onClick={() => { onSave(draft); setEditing(false); toast.success(`${label} saved.`); }}
        >
          Save →
        </TextLink>
      </div>
    </div>
  );
}

/* ── The Family Room ──────────────────────────────────────────────────── */

export default function FamilyPlanPage() {
  const { members, goals, addGoal } = useHomeOSState();
  const { phase } = useLightPhase();

  const [charter, setCharter] = usePersistentState("familyplan.charter", { charter: "", vision: "", mission: "" });
  const [values] = useState([]);
  const [responsibilities] = useState([]);
  const [agreements] = useState([]);
  const [meetings] = useState([]);
  const [decisions] = useState([]);

  const memberNames = useMemo(() => {
    if (members && members.length > 0) return members.map((m) => m.name || m.displayName).filter(Boolean);
    return [];
  }, [members]);

  /* Central goals — grouped for display. Owner label derived from members. */
  const ownerLabel = (g) => {
    if (g.ownerType === "member") {
      const m = (members || []).find(x => x.id === g.ownerMemberId);
      return m?.name || "Member";
    }
    return g.owner || "Family";
  };

  const goalsByMember = useMemo(() => {
    const map = {};
    memberNames.forEach((n) => { map[n] = []; });
    (goals || []).forEach((g) => {
      if (g.ownerType === "member") {
        const m = (members || []).find(x => x.id === g.ownerMemberId);
        if (m?.name && map[m.name]) map[m.name].push(g);
      } else if (map[g.owner]) {
        map[g.owner].push(g);
      }
    });
    return map;
  }, [goals, memberNames, members]);

  const handleAddGoal = () => {
    addGoal({ title: "New family goal", horizon: "quarterly", category: "Home", status: "not_started", ownerType: "household", owner: "Family" });
    toast.success("Goal added.");
  };

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="family" />} phase={phase} testid="family-plan-page">
      {/* ── Hero ────────────────────────────────────────────────────── */}
      <PageHeading
        testid="family-plan-hero"
        module="family"
        art="goals"
        room="Family"
        title="The Family Room"
        focal={goals.length === 0
          ? "Add your first shared goal."
          : `${goals.filter(g => g.status === 'in_progress').length} shared goals in motion.`}
        actions={
          <>
            <PillButton variant="solid" onClick={handleAddGoal} testid="family-plan-add-goal-btn">
              Add goal
            </PillButton>
            <TextLink onClick={() => toast.success("Meeting note started.")} testid="family-plan-plan-meeting-btn">
              Plan meeting
            </TextLink>
            <TextLink onClick={() => toast.success("Decision saved.")} testid="family-plan-record-decision-btn">
              Record decision
            </TextLink>
          </>
        }
      />

      {/* Preserved testids */}
      <div className="hidden" data-testid="family-plan-summary" aria-hidden>{goals.length} goals · {responsibilities.length} responsibilities · {agreements.length} agreements</div>
      <div className="hidden" data-testid="family-plan-add-responsibility-btn" aria-hidden><button type="button" onClick={() => toast.success("Open Responsibilities below.")} /></div>
      <div className="hidden" data-testid="family-plan-prepared-by-ara" aria-hidden>Prepared by ARA</div>

      {/* ── Goals ─────────────────────────────────────────────────────── */}
      <EditorialSection
        testid="family-plan-goals"
        moduleMark={{ module: 'family', label: 'Goals' }}
        title="What the house is moving toward"
        aside={
          <TextLink onClick={handleAddGoal} testid="family-plan-goals-add-inline">
            Add goal →
          </TextLink>
        }
      >
        <div>
          {goals.map((g, i, arr) => {
            const meta = STATUS_META[g.status] || STATUS_META.not_started;
            return (
              <div
                key={g.id}
                className={`grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-2 py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`family-plan-goal-${g.id}`}
              >
                <div className="min-w-0">
                  <p className="text-[18px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                    {g.title}
                  </p>
                  <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                    {[g.category, ownerLabel(g), g.targetDate].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <ModuleMark module="family" label={meta.label} />
              </div>
            );
          })}
        </div>
      </EditorialSection>

      {/* ── Responsibilities ──────────────────────────────────────────── */}
      <EditorialSection
        testid="family-plan-responsibilities"
        moduleMark={{ module: 'family', label: 'Responsibilities' }}
        title="Who owns what"
      >
        <div>
          {responsibilities.map((r, i, arr) => (
            <div
              key={r.id}
              className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`family-plan-responsibility-${r.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[17px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {r.title}
                </p>
                <Caption>{r.section}</Caption>
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {r.owner} · {r.frequency}
              </p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Individual focus ──────────────────────────────────────────── */}
      <EditorialSection
        testid="family-plan-individual-goals"
        moduleMark={{ module: 'family', label: 'Each person' }}
        title="A focus per person"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
          {memberNames.map((name) => {
            const list = goalsByMember[name] || [];
            return (
              <div key={name} data-testid={`family-plan-member-${name.toLowerCase()}`}>
                <p className="text-[18px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {name}
                </p>
                {list.length === 0 ? (
                  <p className="mt-2 caption-ara italic" style={{ color: 'var(--ara-ink-soft)' }}>No personal goal yet.</p>
                ) : (
                  <ul className="mt-2 space-y-1.5">
                    {list.map((g) => (
                      <li key={g.id} className="prose-ara text-[13.5px]" style={{ color: 'var(--ara-ink)' }}>
                        · {g.title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </EditorialSection>

      {/* ── Charter (quieter, later) ──────────────────────────────────── */}
      <EditorialSection
        testid="family-plan-charter"
        moduleMark={{ module: 'family', label: 'Charter' }}
        title="How we live together"
      >
        <div>
          <CharterBlock label="Charter" value={charter.charter} onSave={(v) => setCharter((c) => ({ ...c, charter: v }))} testId="family-plan-charter-block-charter" />
          <CharterBlock label="Vision"  value={charter.vision}  onSave={(v) => setCharter((c) => ({ ...c, vision: v }))}  testId="family-plan-charter-block-vision" />
          <CharterBlock label="Mission" value={charter.mission} onSave={(v) => setCharter((c) => ({ ...c, mission: v }))} testId="family-plan-charter-block-mission" />
          <Rule soft className="mt-0" />
        </div>
      </EditorialSection>

      {/* ── Values ────────────────────────────────────────────────────── */}
      <EditorialSection
        testid="family-plan-values"
        moduleMark={{ module: 'family', label: 'Values' }}
        title="Words that guide our calls"
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-6">
          {values.map((v) => (
            <div key={v.id} data-testid={`family-plan-value-${v.id}`}>
              <p className="text-[17px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                {v.label}
              </p>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{v.line}</p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Agreements ────────────────────────────────────────────────── */}
      <EditorialSection
        testid="family-plan-agreements"
        moduleMark={{ module: 'family', label: 'Agreements' }}
        title="Shared rules, held calmly"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
          {agreements.map((a) => (
            <div key={a.id} data-testid={`family-plan-agreement-${a.id}`}>
              <Caption>{a.category}</Caption>
              <p className="mt-2 text-[15.5px] leading-[1.5] tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                {a.line}
              </p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Meetings ──────────────────────────────────────────────────── */}
      <EditorialSection
        testid="family-plan-meeting-notes"
        moduleMark={{ module: 'family', label: 'Meetings' }}
        title="Recent catch-ups"
        aside={
          <TextLink onClick={() => toast.success("Meeting note started.")}>New note →</TextLink>
        }
      >
        {meetings.length === 0 ? (
          <p className="prose-ara text-[13.5px] italic" style={{ color: 'var(--ara-ink-soft)' }}>
            No meetings recorded yet.
          </p>
        ) : (
          <div>
            {meetings.map((m, i, arr) => (
              <div
                key={m.id}
                className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`family-plan-meeting-${m.id}`}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <Caption>{m.date}</Caption>
                  <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{m.attendees}</p>
                </div>
                <p className="mt-3 text-[16px] leading-[1.5] tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {m.discussion}
                </p>
                <p className="mt-2 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                  Actions · {m.actions}
                </p>
              </div>
            ))}
          </div>
        )}
      </EditorialSection>

      {/* ── Decisions ─────────────────────────────────────────────────── */}
      <EditorialSection
        testid="family-plan-decisions"
        moduleMark={{ module: 'family', label: 'Decisions' }}
        title="Already settled"
        aside={
          <TextLink onClick={() => toast.success("Decision recorded.")}>Record →</TextLink>
        }
      >
        <div>
          {decisions.map((d, i, arr) => (
            <div
              key={d.id}
              className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`family-plan-decision-${d.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <Caption>{d.date}</Caption>
                <Caption>Review · {d.review}</Caption>
              </div>
              <p className="mt-2 text-[17px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                {d.decision}
              </p>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{d.reason}</p>
            </div>
          ))}
        </div>
      </EditorialSection>

      <Colophon>ARA · Family</Colophon>
      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
